import { useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { AdminPageHeader } from '@/features/admin/components/AdminPageHeader';
import { financeShellSx, financeTabsSx } from '@/features/admin-finance/styles/financeSurfaces';
import { RewardsToastProvider } from '@/features/guide-rewards/components/RewardsToast';
import { GrowthApprovalsPanel } from '../components/GrowthApprovalsPanel';
import { GrowthActivitiesPanel, GrowthCampaignsPanel } from '../components/GrowthConfigPanels';
import { GrowthAnalyticsPanel, GrowthOverviewPanel } from '../components/GrowthInsightsPanels';
import { GrowthAuditPanel, GrowthCreditsPanel } from '../components/GrowthLedgerPanels';
import { GrowthMerchPanel } from '../components/GrowthMerchPanel';
import { GrowthPhotosPanel } from '../components/GrowthPhotosPanel';
import { GrowthReferralsPanel } from '../components/GrowthReferralsPanel';

const growthTabs = [
  'overview',
  'referrals',
  'activities',
  'campaigns',
  'approvals',
  'photos',
  'merchandise',
  'credits',
  'analytics',
  'audit',
] as const;

type GrowthTabKey = (typeof growthTabs)[number];

const tabLabels: Record<GrowthTabKey, string> = {
  overview: 'Overview',
  referrals: 'Referrals',
  activities: 'Reward Activities',
  campaigns: 'Campaigns',
  approvals: 'Reward Approvals',
  photos: 'Photo Submissions',
  merchandise: 'Merchandise',
  credits: 'Credits & Transactions',
  analytics: 'Analytics',
  audit: 'Audit Logs',
};

function getGrowthTab(tab: string | null): GrowthTabKey {
  return growthTabs.includes(tab as GrowthTabKey) ? (tab as GrowthTabKey) : 'overview';
}

export function AdminGrowthPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = getGrowthTab(searchParams.get('tab'));

  const setTab = (tab: GrowthTabKey) => {
    setSearchParams(tab === 'overview' ? {} : { tab }, { replace: true });
  };

  return (
    <RewardsToastProvider>
      <Box sx={{ maxWidth: 1280, mx: 'auto' }}>
        <AdminPageHeader
          title="Growth & Rewards"
          subtitle="Guide referrals, reward activities, approvals, merchandise and the credits ledger."
        />
        <Box sx={financeShellSx}>
          <Tabs
            value={activeTab}
            onChange={(_, value: GrowthTabKey) => setTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={financeTabsSx}
          >
            {growthTabs.map((tab) => (
              <Tab key={tab} value={tab} label={tabLabels[tab]} disableRipple />
            ))}
          </Tabs>
          <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
            {activeTab === 'overview' && <GrowthOverviewPanel onNavigate={(tab) => setTab(getGrowthTab(tab))} />}
            {activeTab === 'referrals' && <GrowthReferralsPanel />}
            {activeTab === 'activities' && <GrowthActivitiesPanel />}
            {activeTab === 'campaigns' && <GrowthCampaignsPanel />}
            {activeTab === 'approvals' && <GrowthApprovalsPanel />}
            {activeTab === 'photos' && <GrowthPhotosPanel />}
            {activeTab === 'merchandise' && <GrowthMerchPanel />}
            {activeTab === 'credits' && <GrowthCreditsPanel />}
            {activeTab === 'analytics' && <GrowthAnalyticsPanel />}
            {activeTab === 'audit' && <GrowthAuditPanel />}
          </Box>
        </Box>
      </Box>
    </RewardsToastProvider>
  );
}
